// ── CAPR S1 — the decision vocabulary, declared ONCE (D-CAPR-STATUS-SET) ───────────────────────
//
// The values a `cts_assessment` decision row may carry. The database CHECKs (V1085, V1088) are the
// backstop; these are the names the api writes and later surfaces read. Two homes for one
// vocabulary is how it drifts, so nothing restates these lists.

/**
 * ⚠ THE IMPLEMENTATION STATUS — ONE AXIS, THE V1002 PLATFORM SET. There is NO stored "not started":
 * "Not yet assessed" is the ABSENCE of an approved decision, derived, never a value (D-CAPR-STATUS-SET).
 */
export const CTS_IMPLEMENTATION_STATUSES = [
  'implemented',
  'partially_implemented',
  'not_implemented',
  'not_applicable',
  'unknown',
] as const;
export type CtsImplementationStatus = typeof CTS_IMPLEMENTATION_STATUSES[number];

export const CTS_IMPLEMENTATION_STATUS_LABEL: Record<CtsImplementationStatus, string> = {
  implemented: 'Implemented',
  partially_implemented: 'Partially Implemented',
  not_implemented: 'Not Implemented',
  not_applicable: 'Not Applicable',
  unknown: 'Unknown',
};

/**
 * ⚠ CAPR S2 — THE STATUS HELP TEXT, from MI's clarifications in D-CAPR-STATUS-SET. Shown beside the
 * two values whose meaning is most often misread: "Partially" is not "remediation underway", and
 * "Unknown" is an ASSESSED outcome, not the absence of one. The other three are self-describing.
 */
export const CTS_IMPLEMENTATION_STATUS_HELP: Partial<Record<CtsImplementationStatus, string>> = {
  partially_implemented: 'Some, but not all, requirements are satisfied. It does not necessarily mean remediation is underway.',
  unknown: 'Assessed, but the status is indeterminate; the rationale explains why. It is distinct from both Not Yet Assessed and Implemented.',
};

/** The DERIVED label for a control with no approved decision. Never stored; distinct from every value. */
export const CTS_NOT_YET_ASSESSED_LABEL = 'Not Yet Assessed';

/** The two event kinds of the decision ledger. */
export const CTS_DECISION_OUTCOMES = ['approved', 'returned'] as const;
export type CtsDecisionOutcome = typeof CTS_DECISION_OUTCOMES[number];

/** Why an assessment was started (the brief's choices). S1 records it when given; S2 initiation supplies it. */
export const CTS_REASONS_STARTED = [
  'scheduled_review',
  'initial_assessment',
  'evidence_changed',
  'incident_or_finding',
  'audit_request',
  'other',
] as const;
export type CtsReasonStarted = typeof CTS_REASONS_STARTED[number];

/** CAPR S2 — the rendered names of the six reasons (the brief's choices), one definition for every surface. */
export const CTS_REASON_STARTED_LABEL: Record<CtsReasonStarted, string> = {
  scheduled_review: 'Scheduled review',
  initial_assessment: 'Initial assessment',
  evidence_changed: 'Evidence changed',
  incident_or_finding: 'Incident or finding',
  audit_request: 'Audit request',
  other: 'Other',
};

/**
 * ⚠ EVIDENCE-BASIS VERIFICATION (CAPR S4 activates it). Each item an approval relies on is recorded
 * as one of two statuses, decided by the server at the approve boundary:
 *   • `verified_accepted` — the evidence was present and integrity-confirmed AT APPROVAL: available,
 *     a conclusive check matching what was captured, and not past the catalogue's review advice. It
 *     asserts availability, integrity and recency ONLY — never that the evidence is adequate.
 *   • `unverified_at_approval` — anything else, including unknown and inconclusive. It is approvable,
 *     and it is RECORDED as relied on unverified, never as part of the accepted basis.
 * The acknowledgment of record on every item is the ASSESSOR's note, written at submit
 * (`acknowledgment_note`, with `acknowledged_by`). The approver records nothing about evidence: an
 * approval approves the proposal as submitted. (S1 and S2 wrote every item unverified.)
 */
export const CTS_EVIDENCE_VERIFICATION_STATUSES = ['verified_accepted', 'unverified_at_approval'] as const;
export type CtsEvidenceVerificationStatus = typeof CTS_EVIDENCE_VERIFICATION_STATUSES[number];

/** CAPR S4 — the words for the two statuses, one definition for every surface and every refusal. */
export const CTS_EVIDENCE_VERIFICATION_LABEL: Record<CtsEvidenceVerificationStatus, string> = {
  verified_accepted: 'Evidence present & integrity-confirmed at approval',
  unverified_at_approval: 'Unverified at approval',
};

// ── CAPR S4 — the evidence signals the approve boundary reads (D-CAPR-S4-VERIFIED-PREDICATE) ──
//
// ⚠ COMPUTED BY THE SERVER, ONCE PER READ, AND NEVER BY A SURFACE. A surface renders the status and
//   the reasons it is sent; it never derives either from these values.

/** `cts_evidence_availability` (V1084), with `unknown` for an artifact the caller cannot see. */
export const CTS_EVIDENCE_AVAILABILITY_SIGNALS = ['current', 'inaccessible', 'deleted_at_source', 'unknown'] as const;
export type CtsEvidenceAvailabilitySignal = typeof CTS_EVIDENCE_AVAILABILITY_SIGNALS[number];

/**
 * The newest conclusive content check, judged against THIS capture on the hash:
 * `confirmed` its observed content hash equals the captured one (and no different version);
 * `changed` a content or version change since capture (an unreconciled change);
 * `inconclusive` no conclusive content check is recorded, or no hash to compare.
 */
export const CTS_EVIDENCE_INTEGRITY_SIGNALS = ['confirmed', 'changed', 'inconclusive'] as const;
export type CtsEvidenceIntegritySignal = typeof CTS_EVIDENCE_INTEGRITY_SIGNALS[number];

/**
 * The existing `review_recommended` advice (capture age against the catalogue's cadence):
 * `within_cadence` (false) · `review_recommended` (true) · `no_recommendation` (NULL: the cadence is
 * not a period) · `unknown` (no capture date to measure from).
 */
export const CTS_EVIDENCE_RECENCY_SIGNALS = ['within_cadence', 'review_recommended', 'no_recommendation', 'unknown'] as const;
export type CtsEvidenceRecencySignal = typeof CTS_EVIDENCE_RECENCY_SIGNALS[number];

/** The artifact's last REPORTED change (a signal-raising content or version row) — its identity. */
export interface CtsEvidenceReportedChange {
  /** The integrity-history row id: what "the same change" means between submit and approve. */
  id: string;
  observed_at: string;
  content_hash: string | null;
  repo_version_id: string | null;
}

/** What the server saw for one item at one instant. Recorded append-only in the approved basis. */
export interface CtsEvidenceSignals {
  availability: CtsEvidenceAvailabilitySignal;
  integrity: CtsEvidenceIntegritySignal;
  recency: CtsEvidenceRecencySignal;
  /**
   * ⚠ CONTEXT ONLY, NEVER A TERM IN THE STATUS. Whether a change was reported for this artifact after
   * the control's previous approval relied on it. NULL = the previous approval did not rely on it (or
   * there is none). A prior change never bars verification: a later conclusive check reconciles it.
   */
  changed_since_prior: boolean | null;
  /** The newest conclusive content check the integrity signal was read from, or null when none. */
  newest_check: { change_kind: string; observed_at: string } | null;
  /**
   * ⚠ CAPR S4 (SCR, fail-closed) — how the item's LOCATION has been rechecked since the item's newest
   * row. `recorded_a_check: false` — the latest complete recheck, or a later scheduled recheck run of
   * the binding that began after the location fell due, recorded no check for this item; `overdue` —
   * the location is a whole interval past due and nothing was recorded since it fell due. Either makes
   * integrity `inconclusive`. NULL = the item has no location (nothing rechecks it).
   */
  recheck_coverage: {
    latest_complete_at: string | null;
    due_at: string | null;
    latest_attempt_at: string | null;
    recorded_a_check: boolean;
    overdue: boolean;
  } | null;
  reported_change: CtsEvidenceReportedChange | null;
  evaluated_at: string;
}

/** Why an item is unverified — one code per failed term, in a stable order. */
export const CTS_UNVERIFIED_REASONS = [
  'inaccessible', 'deleted_at_source', 'availability_unknown',
  'unreconciled_change', 'not_confirmed_by_latest_recheck', 'location_recheck_overdue', 'integrity_inconclusive',
  'review_recommended', 'recency_unknown',
] as const;
export type CtsUnverifiedReason = typeof CTS_UNVERIFIED_REASONS[number];

export const CTS_UNVERIFIED_REASON_LABEL: Record<CtsUnverifiedReason, string> = {
  inaccessible: 'The file could not be read at its source at the last check',
  deleted_at_source: 'The file was reported deleted at its source',
  availability_unknown: 'Whether the file is still available could not be determined',
  unreconciled_change: 'The file changed after it was captured, and no later check shows it matching again',
  not_confirmed_by_latest_recheck: 'Its current state was not confirmed against the capture: the latest recheck of its location recorded no check for this file',
  location_recheck_overdue: 'Its current state was not confirmed against the capture: its location’s recheck is overdue',
  integrity_inconclusive: 'No conclusive integrity check, with a content hash to compare, is recorded for this file',
  review_recommended: 'The file is older than the catalogue’s recommended review cadence',
  recency_unknown: 'The capture date is unknown, so its age cannot be judged',
};

/** The server's verdict for one item, as every surface receives it. */
export interface CtsEvidenceVerification {
  status: CtsEvidenceVerificationStatus;
  /** Empty exactly when the status is `verified_accepted`. */
  reasons: CtsUnverifiedReason[];
  signals: CtsEvidenceSignals;
}

/**
 * CAPR S4 — an ADVERSE CHANGE between submit and approve that the assessor's acknowledgment did not
 * cover. Any one refuses the approval (409 PROPOSAL_EVIDENCE_ADVERSE_CHANGE); the reviewer RETURNS the
 * work so the assessor can acknowledge it. No signal on its own ever blocks an approval.
 */
export const CTS_ADVERSE_CHANGES = [
  'became_unavailable', 'integrity_became_inconclusive', 'new_unreconciled_change',
  'review_newly_recommended', 'not_recorded_at_submit',
] as const;
export type CtsAdverseChange = typeof CTS_ADVERSE_CHANGES[number];

export const CTS_ADVERSE_CHANGE_LABEL: Record<CtsAdverseChange, string> = {
  became_unavailable: 'it is no longer available at its source',
  integrity_became_inconclusive: 'its integrity can no longer be confirmed',
  new_unreconciled_change: 'a new change to the file has been reported',
  review_newly_recommended: 'it has passed the catalogue’s recommended review cadence',
  not_recorded_at_submit: 'it was submitted before VeriGRC recorded what the assessor acknowledged, and it is now unverified',
};

/** The explicit "No supporting evidence" basis (BR-D1 / BR-E1) — never an absent or empty value. */
export const CTS_NO_SUPPORTING_EVIDENCE = 'no_supporting_evidence' as const;
export const CTS_NO_SUPPORTING_EVIDENCE_LABEL = 'No supporting evidence';

/** One item an approval relied on — identity + the version/context captured AT APPROVAL (BR-D4). */
export interface CtsEvidenceBasisItem {
  evidence_id: string;
  name: string | null;
  location: string | null;
  captured_version: string | null;
  captured_context: Record<string, unknown>;
  verification_status: CtsEvidenceVerificationStatus;
  /** The ASSESSOR's acknowledgment, written at submit (never the approver's). */
  acknowledgment_note: string | null;
  /**
   * ⚠ CAPR S4 — NULL on a decision recorded before S4, which wrote none of these. The read model
   * returns every key on every item, so a surface never meets an absent one.
   */
  acknowledged_by: string | null;
  signals_at_approval: CtsEvidenceSignals | null;
  verification_reasons: CtsUnverifiedReason[] | null;
}

/** The whole basis: the explicit "none", or one or more items. */
export type CtsEvidenceBasis =
  | { kind: typeof CTS_NO_SUPPORTING_EVIDENCE; items: [] }
  | { kind: 'items'; items: CtsEvidenceBasisItem[] };
